import {
  createPriorityOfFulfillments,
  getCurrentlyAvailable,
  updateAvailableFulfillments,
  getFulfillmentLocation,
  getFulfillmentMethodFromCart
} from './promo-fulfillment-utils';

const emptyPromoCart = {
  promoCartValue: 0,
  promoCartQuantity: 0,
  promoCartItemsObj: {},
  promoSuccessLevel: '',
  hasAnchorItemInCart: false,
  freeItemInCart: null,
};

const cartIsEmpty = (rawCartModel) => rawCartModel?.messagesModel?.[0]?.shortDescription === 'Empty Shopping Cart';
const isValidRawCart = (CartModel) => {
  return !!CartModel && (!!CartModel.itemModels || cartIsEmpty(CartModel));
};

/**
   *  TODO: need to differentitate fetch from update
   * fetch can successfully return itemModels alongisde an errorModel
   * some of the itemModels are good, others may be associated with the cart error
   * errorModel messages will have a correlationId to associate it with an itemModel
   * what do we do with this info? so many edge cases. sigh...
   * Seems that any update request for a single item, if it has an error, it did not change quantity in cart
   * need to check above for BOGO multi add though
   */
const cartResponseHandler = (rawCartResponse) => {
  const CartModel = rawCartResponse?.CartModel;

  if (!isValidRawCart(CartModel)) {
    const cartError = CartModel?.errorModel?.[0] || {};
    const errorMessage = new Error(`cart response contains error: ${cartError.errorCode} ${cartError.description}`);
    /* eslint-disable-next-line no-console */
    console.error(errorMessage);

    return Promise.reject(errorMessage);
  }

  return Promise.resolve(CartModel);
};

const cartFunctions = {
  fetchCartHelper: () => {
    if (typeof window === 'undefined') return Promise.reject();

    return window.THDCart.getCartData()
      .then(cartResponseHandler);
  },
  updateCartHelper: (CartRequest) => {
    if (typeof window === 'undefined') return Promise.reject();

    return window.THDCart.updateCartData({ CartRequest })
      .then(cartResponseHandler);
  },
  addToCartHelper: (CartRequest) => {
    if (typeof window === 'undefined') return Promise.reject();

    return window.THDCart.addToCart({ CartRequest })
      .then(cartResponseHandler);
  },
};

const getHost = () => {
  let host = 'https://www.homedepot.com';
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    host = origin.match(/local/g) ? 'https://www.homedepot.com' : origin;
  }
  return host;
};

const generateAddToCartItemDetail = ({
  itemId, fulfillmentMethod, store, quantity, displayPosition
}) => {
  const fulfillmentLocation = getFulfillmentLocation({ fulfillmentMethod, store });

  return {
    fulfillmentMethod,
    fulfillmentLocation,
    quantity,
    itemId,
    displayPosition,
  };
};

const buildAddToCartRequest = ({
  product, promoItemModel, desiredQuantity = 1, store, selectedFulfillment = ''
}) => {
  const { itemId, displayPosition = '' } = product;
  const fulfillmentPriorities = createPriorityOfFulfillments(selectedFulfillment);
  const currentlyAvailable = getCurrentlyAvailable(promoItemModel, product);
  let neededToFulfill = desiredQuantity;

  const itemDetails = fulfillmentPriorities
    .reduce((items, fulfillmentMethod) => {
      const availableQty = currentlyAvailable[fulfillmentMethod] || 0;
      const quantity = Math.min(neededToFulfill, availableQty);

      if (quantity > 0) {
        neededToFulfill -= quantity;
        items.push(generateAddToCartItemDetail({
          itemId, fulfillmentMethod, store, quantity, displayPosition,
        }));
      }

      return items;
    }, []);

  return { CartRequest: { itemDetails, localStoreId: store.storeId } };
};

const buildAddToCartOverlayOptions = (channel, overrides = {}) => {
  return {
    channel,
    hidden: true,
    misship: false,
    paypal: false,
    host: getHost(),
    ...overrides
  };
};

const buildUpdateCartRequest = ({
  product, promoItemModel, desiredQuantity, store, selectedFulfillment = ''
}) => {
  let itemDetails = [];
  let neededToFulfill = desiredQuantity - (promoItemModel?.quantity || 0);
  const fulfillmentPriorities = createPriorityOfFulfillments(selectedFulfillment);

  if (neededToFulfill > 0) {
    let currentlyAvailable = getCurrentlyAvailable(promoItemModel, product);

    // Updating or adding quantities to cart
    itemDetails = fulfillmentPriorities.reduce((items, fulfillmentMethod) => {
      const availableQty = currentlyAvailable[fulfillmentMethod];
      if (!availableQty || neededToFulfill <= 0) return items;

      // Updating item
      const lineItem = promoItemModel.fulfillments[fulfillmentMethod];
      if (lineItem && lineItem.id && lineItem.quantity) {
        const qtyChange = Math.min(neededToFulfill, availableQty);
        neededToFulfill -= qtyChange;
        currentlyAvailable = updateAvailableFulfillments(fulfillmentMethod, currentlyAvailable, qtyChange);

        items.push({
          itemId: promoItemModel.itemId,
          quantity: qtyChange + lineItem.quantity,
          lineItemId: lineItem.id,
          fulfillmentMethod,
          fulfillmentLocation: getFulfillmentLocation({ fulfillmentMethod, store })
        });
      }

      return items;
    }, []);

  } else if (neededToFulfill < 0) {
    // reduceRight will subtract values from low-pri to high-pri fulfillments
    itemDetails = fulfillmentPriorities.reduceRight((items, fulfillmentMethod) => {
      if (neededToFulfill >= 0) return items;

      const lineItem = promoItemModel.fulfillments[fulfillmentMethod];
      if (lineItem && lineItem.id && lineItem.quantity) {
        const quantity = Math.max(neededToFulfill + lineItem.quantity, 0);

        if (quantity < lineItem.quantity) {
          neededToFulfill -= quantity - lineItem.quantity;
          items.push({
            itemId: promoItemModel.itemId,
            quantity,
            lineItemId: lineItem.id,
            fulfillmentMethod,
            fulfillmentLocation: getFulfillmentLocation({ fulfillmentMethod, store })
          });
        }
      }

      return items;
    }, []);

  }

  return { CartRequest: { itemDetails, localStoreId: store.storeId } };
};

const getPromoSuccessLevel = ({
  promoCart,
  isBogo,
  isMsb,
  minPurchaseQuantity,
  minPurchaseAmount,
  bogoSelectedProduct,
  freeItemInCart
}) => {

  const {
    promoCartValue, promoCartQuantity, hasAnchorItemInCart
  } = promoCart;

  if (isBogo) {
    if (freeItemInCart && hasAnchorItemInCart) return 'success';
    if (bogoSelectedProduct) return 'almost';
  }

  if (isMsb) {
    if (!promoCartQuantity || !promoCartValue) return '';
    let amountLeft;
    let percentLeft;
    if (minPurchaseQuantity) {
      amountLeft = Math.max(0, minPurchaseQuantity - promoCartQuantity);
      percentLeft = Math.max(0, Math.floor(100 - (promoCartQuantity / minPurchaseQuantity) * 100));
    } else { // minPurchaseAmount
      amountLeft = Math.max(0, minPurchaseAmount - promoCartValue);
      percentLeft = Math.max(0, Math.floor(100 - (promoCartValue / minPurchaseAmount) * 100));
    }
    if (amountLeft === 0 || percentLeft === 0) return 'success';
    if (amountLeft === 1 || percentLeft <= 10) return 'almost';

  }
  return '';
};

const cartItemIsSanitary = (cartItem) => {
  return cartItem
    && cartItem.itemId
    && typeof cartItem.lineItemId === 'string'
    && cartItem.lineItemId.length
    && Number(cartItem.quantity)
    && Number(cartItem.totalItemPrice)
    && Array.isArray(cartItem.fulfillmentModel)
    && cartItem.fulfillmentModel[0]
    && cartItem.fulfillmentModel[0].fulfillmentMethod;
};

const generatePromoCartItemsObj = ({ itemModels = [], allItemIds = [] }) => {
  const promoCartItemsObj = {};
  itemModels
    .filter((cartItem) => cartItemIsSanitary(cartItem) && allItemIds.includes(cartItem.itemId))
    .forEach((promoCartItemEntry) => {
      const image = promoCartItemEntry.image;
      const itemId = promoCartItemEntry.itemId;
      const quantity = Number(promoCartItemEntry.quantity);
      const totalItemPrice = Number(promoCartItemEntry.totalItemPrice);
      const id = promoCartItemEntry.lineItemId;
      const newFulfillmentMethod = getFulfillmentMethodFromCart(promoCartItemEntry);

      if (!promoCartItemsObj[itemId]) {
        promoCartItemsObj[itemId] = {
          itemId,
          image,
          quantity,
          totalItemPrice,
          fulfillments: {
            [newFulfillmentMethod]: {
              quantity,
              id
            }
          }
        };
      } else {
        promoCartItemsObj[itemId].quantity += quantity;
        promoCartItemsObj[itemId].totalItemPrice += totalItemPrice;
        promoCartItemsObj[itemId].fulfillments[newFulfillmentMethod] = { quantity, id };
      }
    });

  return promoCartItemsObj;
};

const transformCartModelToPromoCart = (
  rawCartModel, isBogo, isMsb, minPurchaseQuantity, minPurchaseAmount, allItemIds, anchorItemId, bogoSelectedProduct
) => {
  const newPromoCart = JSON.parse(JSON.stringify(emptyPromoCart));
  const { itemModels } = rawCartModel || {};

  if (Array.isArray(itemModels)) newPromoCart.promoCartItemsObj = generatePromoCartItemsObj({ itemModels, allItemIds });

  if (newPromoCart.promoCartItemsObj?.[anchorItemId]?.quantity) newPromoCart.hasAnchorItemInCart = true;

  Object.values(newPromoCart.promoCartItemsObj).forEach((promoItem) => {
    newPromoCart.promoCartValue += promoItem.totalItemPrice;
    newPromoCart.promoCartQuantity += promoItem.quantity;
  });

  const nonAnchorIdInCart = (allItemIds || [])
    .find((itemId) => (itemId !== anchorItemId && newPromoCart.promoCartItemsObj[itemId]));
  const freeItemInCart = nonAnchorIdInCart ? newPromoCart.promoCartItemsObj[nonAnchorIdInCart] : null;

  newPromoCart.promoSuccessLevel = getPromoSuccessLevel({
    promoCart: newPromoCart,
    isBogo,
    isMsb,
    minPurchaseQuantity,
    minPurchaseAmount,
    bogoSelectedProduct,
    freeItemInCart
  });

  if (isBogo && newPromoCart.promoSuccessLevel === 'success') {
    newPromoCart.freeItemInCart = freeItemInCart;
  }

  return newPromoCart;
};

const getCartAdditions = (oldCartModels, newCartModels) => {
  return newCartModels.filter((newModel) => {
    return !oldCartModels.find((oldModel) => {
      return oldModel.lineItemId === newModel.lineItemId && oldModel.quantity >= newModel.quantity;
    });
  }).map((newModel) => newModel.itemId);
};

const wereItemsJustAddedToCart = (itemIds = [], oldCartModels = [], newCartModels = []) => {
  const additions = getCartAdditions(oldCartModels, newCartModels);

  return !!itemIds.length && itemIds.filter((itemId) => {
    return additions.includes(itemId);
  }).length === itemIds.length;
};

export {
  emptyPromoCart,
  isValidRawCart,
  cartFunctions,
  transformCartModelToPromoCart,
  buildUpdateCartRequest,
  wereItemsJustAddedToCart,
  buildAddToCartRequest,
  buildAddToCartOverlayOptions,
  generatePromoCartItemsObj,
  getHost
};
